"use client";

import { c as _c } from "react-compiler-runtime";
import { Check, ChevronRight, ShieldCheck, TriangleAlert, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import NextImage from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Text } from "@/components/primitives";
import { MediaUploader } from "@/components/ui/MediaUploader/MediaUploader";
import { useAnalytics } from "@/core/analytics";
import { getRetrieveCurrentAssessmentQueryKey } from "@/core/api/orval/ageless-assessments/ageless-assessments";
import { confirmWellnessPhotoCapture, declineWellnessForAssessment, uploadWellnessBodyPhoto, useRetrieveWellnessBodyPhotoValidation } from "@/core/api/orval/ageless-imaging/ageless-imaging";
import { queryClient } from "@/core/caching";
import { type Dictionary, useTranslation } from "@/core/i18n";
import { useI18n } from "@/core/i18n/useI18n/useI18n";
import { logger } from "@/core/logging";
import { usePlatformDataPrivacyRegion } from "@/features/platform/usePlatformDataPrivacyRegion";
import { captureAssessmentConsents, hasAssessmentConsentInCache } from "@/features/assessments/consent/consentUtils";
import { deriveConsentLanguageCode, getBiometricConsentCheckboxPrefixKey, getBiometricConsentItemsForRegion } from "@/features/assessments/consent/regionalConsent";
import { useConsentGate } from "@/features/assessments/consent/useConsentGate";
import { AgelessAssessmentFlowType, AgelessAssessmentStep } from "@orval/model";
import { useCurrentAssessment } from "../../AssessmentSessionProvider";
import { useAssessmentStep } from "../../AssessmentStepProvider/AssessmentStepProvider";
import { useFlowOrder } from "../../useFlowOrder/useFlowOrder";
import { filterSupportedWellnessInterests } from "../../utils/wellnessCapabilityVisibility";
import { ConsentOverlay } from "../ImageCaptureStep/components/ConsentOverlay";
import { useDemoMode } from "../../demo/useDemoMode";
import { DemoPersonaSelector } from "@/features/assessments/demo/DemoPersonaSelector";
import { fetchDemoImageAsFile, WELLNESS_DEMO_PERSONAS } from "@/features/assessments/demo/demoPersonas";
import { WellnessImageEditor } from "./components/WellnessImageEditor";
import { normalizeWellnessImageForEditor, normalizeWellnessImageForUpload } from "./wellnessUpload";
const POLL_INTERVAL = 1_000;
const WELLNESS_VALIDATION_ISSUE_TRANSLATIONS: Partial<Record<string, keyof Dictionary>> = {
  no_person: "wellnessSteps.intake.validationIssue.no_person",
  multiple_people: "wellnessSteps.intake.validationIssue.multiple_people",
  framing_too_tight: "wellnessSteps.intake.validationIssue.framing_too_tight",
  person_too_small: "wellnessSteps.intake.validationIssue.person_too_small",
  unclear_image: "wellnessSteps.intake.validationIssue.unclear_image",
  poorly_lit: "wellnessSteps.intake.validationIssue.poorly_lit",
  inappropriate_content: "wellnessSteps.intake.validationIssue.inappropriate_content",
  underage_person: "wellnessSteps.intake.validationIssue.underage_person",
  validation_unavailable: "wellnessSteps.intake.validationIssue.validation_unavailable"
};
const DEFAULT_WELLNESS_VALIDATION_ISSUE_KEY: keyof Dictionary = "wellnessSteps.intake.validationIssue.validation_unavailable";
type UploadPhase = "guidance" | "cropping" | "validating" | "upload-error";
type WellnessImageCaptureStepProps = {
  /**
   * Whether the combined-flow "skip for now" affordance may appear. The standard funnel leaves
   * this on; the standalone consult-capture page (patient followed a texted link) turns it off so
   * the patient can't skip the photo the provider asked them to take.
   */
  allowSkip?: boolean;
};
export const WellnessImageCaptureStep = t0 => {
  const $ = _c(175);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    allowSkip: t2
  } = t1;
  const allowSkip = t2 === undefined ? true : t2;
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    currentLang
  } = useI18n();
  const {
    goToNextStep,
    goToStep,
    subdomainSlug,
    flowType
  } = useAssessmentStep();
  const {
    flowOrder,
    getFlowOrderSnapshot
  } = useFlowOrder(subdomainSlug, flowType);
  let t3;
  if ($[2] !== flowType || $[3] !== subdomainSlug) {
    t3 = {
      subdomainSlug,
      flowType
    };
    $[2] = flowType;
    $[3] = subdomainSlug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isDemoMode = useDemoMode(t3);
  const {
    assessment,
    headers
  } = useCurrentAssessment();
  const {
    dataPrivacyRegion,
    isPending: isRegionPending
  } = usePlatformDataPrivacyRegion(subdomainSlug);
  let t4;
  bb0: {
    if (!dataPrivacyRegion) {
      t4 = undefined;
      break bb0;
    }
    let t5;
    if ($[5] !== dataPrivacyRegion || $[6] !== t) {
      t5 = getBiometricConsentItemsForRegion(dataPrivacyRegion, t);
      $[5] = dataPrivacyRegion;
      $[6] = t;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = t5;
  }
  const biometricConsentItems = t4;
  let t5;
  if ($[8] !== dataPrivacyRegion) {
    t5 = dataPrivacyRegion ? getBiometricConsentCheckboxPrefixKey(dataPrivacyRegion) : "wellnessSteps.intake.consent.checkboxPrefix";
    $[8] = dataPrivacyRegion;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = {
      title: "wellnessSteps.intake.consent.title",
      body: "wellnessSteps.intake.consent.body",
      highlight: "wellnessSteps.intake.consent.highlight",
      checkboxPrefix: t5,
      checkboxSuffix: "wellnessSteps.intake.consent.checkboxSuffix",
      checkboxDeidentified: "wellnessSteps.intake.consent.checkboxDeidentified",
      privacyLink: "wellnessSteps.intake.consent.privacyLink",
      termsPrefix: "wellnessSteps.intake.consent.termsPrefix",
      termsLink: "wellnessSteps.intake.consent.termsLink",
      termsSuffix: "wellnessSteps.intake.consent.termsSuffix",
      cancel: "wellnessSteps.intake.consent.cancel",
      confirm: "wellnessSteps.intake.consent.confirm"
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const consentTranslationKeys = t6 as const;
  const [uploadPhase, setUploadPhase] = useState("guidance");
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);
  const [pendingCroppedFile, setPendingCroppedFile] = useState(null);
  const [showConsentOverlay, setShowConsentOverlay] = useState(false);
  const originalFileNameRef = useRef(null);
  const [consentDeclined, setConsentDeclined] = useState(false);
  const autoAdvancePhotoIdRef = useRef(null);
  const isPolling = uploadPhase === "validating" && !!photoId;
  let t7;
  if ($[12] !== isPolling) {
    t7 = {
      enabled: isPolling,
      refetchInterval: _temp
    };
    $[12] = isPolling;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== headers) {
    t8 = {
      method: "GET",
      headers
    };
    $[14] = headers;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = {
      query: t7,
      request: t8
    };
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const {
    data: validationData
  } = useRetrieveWellnessBodyPhotoValidation(photoId ?? "", t9);
  let t10;
  if ($[19] !== validationData?.issueKeys) {
    t10 = validationData?.issueKeys ?? [];
    $[19] = validationData?.issueKeys;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const validationIssueKeysList = t10;
  const hasBlockingValidationIssues = validationData?.hasBlockingIssues === true;
  let t11;
  bb1: {
    if (uploadPhase !== "validating") {
      t11 = uploadPhase;
      break bb1;
    }
    if (!validationData?.isValidated) {
      t11 = "validating";
      break bb1;
    }
    if (hasBlockingValidationIssues) {
      t11 = "validation-error";
      break bb1;
    }
    if (validationData.isUsable === false) {
      t11 = "validation-warning";
      break bb1;
    }
    t11 = "validated";
  }
  const resolvedPhase = t11;
  const showsValidationIssues = resolvedPhase === "validation-error" || resolvedPhase === "validation-warning";
  const validationIssueKeys = showsValidationIssues && validationData ? validationData.issueKeys : [];
  const translatedValidationIssues = showsValidationIssues ? validationIssueKeys.map(issueKey => t(WELLNESS_VALIDATION_ISSUE_TRANSLATIONS[issueKey] ?? DEFAULT_WELLNESS_VALIDATION_ISSUE_KEY)) : [];
  const validationErrorHeadline = translatedValidationIssues[0];
  const validationIssues = translatedValidationIssues.length > 1 ? translatedValidationIssues.slice(1) : [];
  let t12;
  bb2: {
    if (resolvedPhase === "upload-error") {
      t12 = uploadErrorMessage;
      break bb2;
    }
    if (resolvedPhase === "validation-error") {
      t12 = validationErrorHeadline || t("wellnessSteps.intake.defaultError");
      break bb2;
    }
    t12 = null;
  }
  const displayErrorMessage = t12;
  let t13;
  if ($[21] !== assessment || $[22] !== headers || $[23] !== t || $[24] !== thumbnailUrl) {
    t13 = async file => {
      if (!assessment) {
        return;
      }
      const {
        id: assessmentId,
        platformId
      } = assessment;
      setUploadPhase("validating");
      let uploadFile;
      ;
      try {
        uploadFile = await normalizeWellnessImageForUpload(file);
      } catch (t14) {
        const error = t14;
        logger.warn("wellness_image_compression_failed");
        logger.error("wellness_image_compression_failed_error", undefined, error as Error);
        setUploadErrorMessage(t("wellnessSteps.intake.uploadFailed"));
        setUploadPhase("upload-error");
        return;
      }
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
      const thumbUrl = URL.createObjectURL(uploadFile);
      setThumbnailUrl(thumbUrl);
      try {
        const response = await uploadWellnessBodyPhoto(assessmentId, platformId, {
          File: uploadFile
        }, {
          headers
        });
        setPhotoId(response.wellnessBodyPhotoId);
      } catch {
        setUploadErrorMessage(t("wellnessSteps.intake.uploadFailed"));
        setUploadPhase("upload-error");
      }
    };
    $[21] = assessment;
    $[22] = headers;
    $[23] = t;
    $[24] = thumbnailUrl;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const performUpload = t13;
  let t14;
  if ($[26] !== rawImageSrc || $[27] !== t) {
    t14 = async file_0 => {
      setUploadErrorMessage(null);
      originalFileNameRef.current = file_0.name;
      ;
      try {
        const normalizedFile = await normalizeWellnessImageForEditor(file_0);
        if (rawImageSrc) {
          URL.revokeObjectURL(rawImageSrc);
        }
        const objectUrl = URL.createObjectURL(normalizedFile);
        setRawImageSrc(objectUrl);
        setUploadPhase("cropping");
      } catch (t15) {
        logger.warn("wellness_image_normalization_failed");
        setUploadErrorMessage(t("wellnessSteps.intake.uploadFailed"));
        setUploadPhase("upload-error");
      }
    };
    $[26] = rawImageSrc;
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const handleFileSelected = t14;
  let t15;
  if ($[29] !== assessment || $[30] !== flowType || $[31] !== performUpload || $[32] !== rawImageSrc || $[33] !== thumbnailUrl) {
    t15 = async croppedFile => {
      if (!assessment) {
        return;
      }
      if (rawImageSrc) {
        URL.revokeObjectURL(rawImageSrc);
      }
      setRawImageSrc(null);
      const fileName = originalFileNameRef.current ?? croppedFile.name;
      const fileWithOriginalName = new File([croppedFile], fileName, {
        type: croppedFile.type,
        lastModified: croppedFile.lastModified
      });
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
      const thumbUrl_0 = URL.createObjectURL(fileWithOriginalName);
      setThumbnailUrl(thumbUrl_0);
      if (hasAssessmentConsentInCache(assessment.id, flowType)) {
        await performUpload(fileWithOriginalName);
      } else {
        setPendingCroppedFile(fileWithOriginalName);
        setShowConsentOverlay(true);
      }
    };
    $[29] = assessment;
    $[30] = flowType;
    $[31] = performUpload;
    $[32] = rawImageSrc;
    $[33] = thumbnailUrl;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const handleCropComplete = t15;
  let t16;
  if ($[35] !== assessment || $[36] !== currentLang || $[37] !== dataPrivacyRegion || $[38] !== flowType || $[39] !== headers || $[40] !== pendingCroppedFile || $[41] !== performUpload) {
    t16 = async () => {
      if (!assessment || !pendingCroppedFile) {
        return;
      }
      if (!dataPrivacyRegion) {
        return;
      }
      const languageCode = deriveConsentLanguageCode(currentLang);
      await captureAssessmentConsents(assessment.id, flowType, languageCode, dataPrivacyRegion, {
        headers
      });
      setShowConsentOverlay(false);
      await performUpload(pendingCroppedFile);
    };
    $[35] = assessment;
    $[36] = currentLang;
    $[37] = dataPrivacyRegion;
    $[38] = flowType;
    $[39] = headers;
    $[40] = pendingCroppedFile;
    $[41] = performUpload;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const handleConsentAccepted = t16;
  let t17;
  let t18;
  if ($[43] !== analytics || $[44] !== flowType) {
    t17 = () => {
      analytics.trackEvent("assessment_data_capture_consent_declined", {
        flowType
      });
      setShowConsentOverlay(false);
      setConsentDeclined(true);
    };
    t18 = () => {
      analytics.trackEvent("assessment_data_capture_consent_given", {
        flowType
      });
    };
    $[43] = analytics;
    $[44] = flowType;
    $[45] = t17;
    $[46] = t18;
  } else {
    t17 = $[45];
    t18 = $[46];
  }
  let t19;
  if ($[47] !== handleConsentAccepted || $[48] !== t17 || $[49] !== t18) {
    t19 = {
      onCancel: t17,
      trackConsent: t18,
      onConsentAccepted: handleConsentAccepted
    };
    $[47] = handleConsentAccepted;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const consent = useConsentGate(t19);
  let t20;
  if ($[51] !== rawImageSrc) {
    t20 = () => {
      if (rawImageSrc) {
        URL.revokeObjectURL(rawImageSrc);
      }
      setRawImageSrc(null);
      setUploadPhase("guidance");
    };
    $[51] = rawImageSrc;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const handleCropCancel = t20;
  let t21;
  if ($[53] !== assessment || $[54] !== goToNextStep || $[55] !== headers || $[56] !== photoId || $[57] !== t) {
    t21 = async () => {
      if (!assessment || !photoId) {
        return;
      }
      const previewCategories = filterSupportedWellnessInterests(assessment.wellnessInterests);
      try {
        await confirmWellnessPhotoCapture(assessment.id, {
          wellnessBodyPhotoId: photoId,
          wellnessCategories: previewCategories
        }, {
          headers
        });
        await queryClient.invalidateQueries({
          queryKey: getRetrieveCurrentAssessmentQueryKey(),
          refetchType: "none"
        });
        goToNextStep();
      } catch {
        setUploadErrorMessage(t("wellnessSteps.intake.defaultError"));
        setUploadPhase("upload-error");
      }
    };
    $[53] = assessment;
    $[54] = goToNextStep;
    $[55] = headers;
    $[56] = photoId;
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const handleGeneratePreview = t21;
  let t22;
  if ($[59] !== analytics || $[60] !== assessment || $[61] !== flowType || $[62] !== photoId || $[63] !== rawImageSrc || $[64] !== thumbnailUrl) {
    t22 = () => {
      if (assessment && (rawImageSrc || thumbnailUrl || photoId)) {
        analytics.trackEvent("assessment_photo_retake_requested", {
          assessmentId: assessment.id,
          flowType,
          photoType: "body",
          source: "wellness_capture"
        });
      }
      if (rawImageSrc) {
        URL.revokeObjectURL(rawImageSrc);
      }
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
      setUploadPhase("guidance");
      setRawImageSrc(null);
      setThumbnailUrl(null);
      setPhotoId(null);
      setUploadErrorMessage(null);
      setPendingCroppedFile(null);
      setShowConsentOverlay(false);
      setConsentDeclined(false);
      autoAdvancePhotoIdRef.current = null;
      originalFileNameRef.current = null;
    };
    $[59] = analytics;
    $[60] = assessment;
    $[61] = flowType;
    $[62] = photoId;
    $[63] = rawImageSrc;
    $[64] = thumbnailUrl;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  const resetToGuidance = t22;
  let t23;
  let t24;
  if ($[66] !== handleGeneratePreview || $[67] !== photoId || $[68] !== resolvedPhase) {
    t23 = () => {
      if (resolvedPhase !== "validated" || !photoId) {
        return;
      }
      if (autoAdvancePhotoIdRef.current === photoId) {
        return;
      }
      autoAdvancePhotoIdRef.current = photoId;
      handleGeneratePreview();
    };
    t24 = [handleGeneratePreview, photoId, resolvedPhase];
    $[66] = handleGeneratePreview;
    $[67] = photoId;
    $[68] = resolvedPhase;
    $[69] = t23;
    $[70] = t24;
  } else {
    t23 = $[69];
    t24 = $[70];
  }
  useEffect(t23, t24);
  if (!assessment) {
    return null;
  }
  const isCombinedFlow = flowType === AgelessAssessmentFlowType.AestheticsWellness;
  const showSkipForNow = isCombinedFlow && allowSkip;
  let t25;
  if ($[71] !== analytics || $[72] !== assessment.id || $[73] !== flowOrder || $[74] !== flowType || $[75] !== getFlowOrderSnapshot || $[76] !== goToStep || $[77] !== headers || $[78] !== t) {
    t25 = async () => {
      analytics.trackEvent("assessment_wellness_photo_skipped", {
        flowType,
        assessmentId: assessment.id
      });
      try {
        await declineWellnessForAssessment(assessment.id, {
          headers
        });
      } catch {
        setUploadErrorMessage(t("wellnessSteps.intake.defaultError"));
        setUploadPhase("upload-error");
        return;
      }
      const resolvedFlowOrder = getFlowOrderSnapshot() ?? flowOrder;
      goToStep(resolvedFlowOrder === "BodyFirst" ? AgelessAssessmentStep.ImageCapture : AgelessAssessmentStep.Results);
    };
    $[71] = analytics;
    $[72] = assessment.id;
    $[73] = flowOrder;
    $[74] = flowType;
    $[75] = getFlowOrderSnapshot;
    $[76] = goToStep;
    $[77] = headers;
    $[78] = t;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  const handleSkipWellnessPhoto = t25;
  const isError = resolvedPhase === "upload-error" || resolvedPhase === "validation-error";
  let t26;
  if ($[80] !== analytics || $[81] !== assessment.id || $[82] !== flowType || $[83] !== handleGeneratePreview || $[84] !== photoId || $[85] !== validationIssueKeysList) {
    t26 = () => {
      analytics.trackEvent("assessment_wellness_photo_warning_acknowledged", {
        assessmentId: assessment.id,
        flowType,
        issueKeys: validationIssueKeysList
      });
      autoAdvancePhotoIdRef.current = photoId;
      handleGeneratePreview();
    };
    $[80] = analytics;
    $[81] = assessment.id;
    $[82] = flowType;
    $[83] = handleGeneratePreview;
    $[84] = photoId;
    $[85] = validationIssueKeysList;
    $[86] = t26;
  } else {
    t26 = $[86];
  }
  const handleContinueAnyway = t26;
  let t27;
  if ($[87] !== t) {
    t27 = t("wellnessSteps.intake.tips.fullBody");
    $[87] = t;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== t) {
    t28 = t("wellnessSteps.intake.tips.clothing");
    $[89] = t;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t) {
    t29 = t("wellnessSteps.intake.tips.cropHint");
    $[91] = t;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== t27 || $[94] !== t28 || $[95] !== t29) {
    t30 = [t27, t28, t29];
    $[93] = t27;
    $[94] = t28;
    $[95] = t29;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  const hints = t30;
  let t31;
  if ($[97] !== handleFileSelected) {
    t31 = async personaId => {
      const persona = WELLNESS_DEMO_PERSONAS.find(p => p.id === personaId);
      if (!persona) {
        return;
      }
      const file_1 = await fetchDemoImageAsFile(persona.wellnessImageUrl, persona.wellnessFileName);
      handleFileSelected(file_1);
    };
    $[97] = handleFileSelected;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  const handleDemoSelect = t31;
  let t32;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = WELLNESS_DEMO_PERSONAS.map(_temp2);
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  const wellnessDemoThumbnails = t32;
  let t33;
  if ($[100] !== t) {
    t33 = () => <div className="text-center"><Text component="h1" text={t("wellnessSteps.intake.title")} variant="display-md" /><div className="mt-2"><Text component="p" text={t("wellnessSteps.intake.subtitle")} tone="muted" variant="body-sm" /></div></div>;
    $[100] = t;
    $[101] = t33;
  } else {
    t33 = $[101];
  }
  const renderHeader = t33;
  let t34;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      duration: 0.25,
      ease: "easeInOut" as const
    };
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  const phaseTransition = t34;
  let t35;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      opacity: 0,
      y: 12
    };
    $[103] = t35;
  } else {
    t35 = $[103];
  }
  const phaseInitial = t35;
  let t36;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      opacity: 1,
      y: 0
    };
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  const phaseAnimate = t36;
  let t37;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      opacity: 0,
      y: -8
    };
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  const phaseExit = t37;
  let t38;
  if ($[106] !== handleDemoSelect || $[107] !== handleFileSelected || $[108] !== handleSkipWellnessPhoto || $[109] !== hints || $[110] !== isDemoMode || $[111] !== showSkipForNow || $[112] !== t) {
    t38 = () => <motion.div animate={phaseAnimate} className="flex flex-col gap-6" exit={phaseExit} initial={phaseInitial} key="guidance" transition={phaseTransition}><MediaUploader dropActiveText={t("wellnessSteps.intake.dropPhoto")} hints={hints} promptText={t("wellnessSteps.intake.tapToUpload")} onFileSelected={handleFileSelected} />{isDemoMode && <DemoPersonaSelector personas={wellnessDemoThumbnails} onSelect={handleDemoSelect} fullBody={true} />}{showSkipForNow && <div className="flex w-full justify-center"><Button data-testid="wellness-skip-for-now" size="sm" type="button" variant="ghost" onClick={handleSkipWellnessPhoto}><Text component="span" text={t("wellnessSteps.intake.skipForNow")} variant="body-sm" /><ChevronRight aria-hidden="true" className="h-3.5 w-3.5" /></Button></div>}</motion.div>;
    $[106] = handleDemoSelect;
    $[107] = handleFileSelected;
    $[108] = handleSkipWellnessPhoto;
    $[109] = hints;
    $[110] = isDemoMode;
    $[111] = showSkipForNow;
    $[112] = t;
    $[113] = t38;
  } else {
    t38 = $[113];
  }
  const renderGuidancePhase = t38;
  let t39;
  if ($[114] !== handleCropCancel || $[115] !== handleCropComplete || $[116] !== rawImageSrc) {
    t39 = () => {
      if (!rawImageSrc) {
        return null;
      }
      return <motion.div animate={phaseAnimate} exit={phaseExit} initial={phaseInitial} key="cropping" transition={phaseTransition}><WellnessImageEditor imageSrc={rawImageSrc} onCancel={handleCropCancel} onCropComplete={handleCropComplete} /></motion.div>;
    };
    $[114] = handleCropCancel;
    $[115] = handleCropComplete;
    $[116] = rawImageSrc;
    $[117] = t39;
  } else {
    t39 = $[117];
  }
  const renderCroppingPhase = t39;
  const renderValidationPhase = () => {
    if (!thumbnailUrl) {
      return null;
    }
    return <motion.div animate={phaseAnimate} className="flex flex-col gap-4" exit={phaseExit} initial={phaseInitial} key="validation" transition={phaseTransition}><div className="relative w-full overflow-hidden rounded-2xl"><div className="bg-muted relative aspect-3/4 w-full"><NextImage aria-hidden={true} fill={true} unoptimized={true} alt="" className="scale-110 object-cover opacity-40 blur-xl" src={thumbnailUrl} /><NextImage fill={true} unoptimized={true} alt="Uploaded photo" className="object-contain object-center" src={thumbnailUrl} /></div><div className="absolute inset-x-0 bottom-6 flex justify-center"><AnimatePresence mode="wait">{resolvedPhase === "validating" ? <motion.div animate={{
              opacity: 1,
              scale: 1
            }} className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm" exit={{
              opacity: 0,
              scale: 0.95
            }} initial={{
              opacity: 0,
              scale: 0.95
            }} key="checking" transition={{
              duration: 0.2
            }}><motion.div animate={{
                opacity: [1, 0.4, 1]
              }} className="bg-foreground h-2 w-2 rounded-full" transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }} /><Text component="span" text={t("wellnessSteps.intake.checking")} variant="body-sm" weight="medium" /></motion.div> : resolvedPhase === "validation-warning" ? <motion.div animate={{
              opacity: 1,
              scale: 1
            }} className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm" exit={{
              opacity: 0,
              scale: 0.95
            }} initial={{
              opacity: 0,
              scale: 0.95
            }} key="warning" transition={{
              duration: 0.2
            }}><TriangleAlert className="h-3.5 w-3.5" /><Text component="span" text={t("wellnessSteps.intake.validationWarning.title")} variant="caption" /></motion.div> : <motion.div animate={{
              opacity: 1,
              scale: 1
            }} className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm" exit={{
              opacity: 0,
              scale: 0.95
            }} initial={{
              opacity: 0,
              scale: 0.95
            }} key="verified" transition={{
              duration: 0.2
            }}><Check className="h-3.5 w-3.5" /><Text component="span" text={t("wellnessSteps.intake.verified")} variant="caption" /></motion.div>}</AnimatePresence></div><AnimatePresence>{(resolvedPhase === "validated" || resolvedPhase === "validation-warning") && <motion.button animate={{
            opacity: 1
          }} aria-label="Retake body photo" className="bg-background/80 hover:bg-background absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full shadow-lg backdrop-blur-sm transition-colors" data-testid="wellness-retake-photo" exit={{
            opacity: 0
          }} initial={{
            opacity: 0
          }} type="button" onClick={resetToGuidance}><X className="h-4 w-4" /></motion.button>}</AnimatePresence></div>{resolvedPhase === "validation-warning" && <div className="flex flex-col gap-3" data-testid="wellness-validation-warning"><div className="flex flex-col gap-1 text-center">{(translatedValidationIssues.length > 0 ? translatedValidationIssues : [t(DEFAULT_WELLNESS_VALIDATION_ISSUE_KEY)]).map(_temp3)}</div><Button data-testid="wellness-continue-anyway" type="button" onClick={handleContinueAnyway}><Text component="span" text={t("wellnessSteps.intake.validationWarning.continueAnyway")} /></Button><Button data-testid="wellness-warning-retake" type="button" variant="ghost" onClick={resetToGuidance}><Text component="span" text={t("wellnessSteps.intake.tryDifferent")} /></Button></div>}</motion.div>;
  };
  const renderErrorPhase = () => <motion.div animate={phaseAnimate} className="flex flex-col gap-6" exit={phaseExit} initial={phaseInitial} key="error" transition={phaseTransition}><MediaUploader dropActiveText={t("wellnessSteps.intake.dropPhoto")} errorMessage={displayErrorMessage} promptText={t("wellnessSteps.intake.tapToUpload")} status="error" validationIssues={validationIssues} onFileSelected={handleFileSelected} onReset={resetToGuidance} /></motion.div>;
  let t40;
  if ($[118] !== consent) {
    t40 = () => {
      consent.setConsentChecked(false);
      setConsentDeclined(false);
      setShowConsentOverlay(true);
    };
    $[118] = consent;
    $[119] = t40;
  } else {
    t40 = $[119];
  }
  const handleGrantPermission = t40;
  let t41;
  if ($[120] !== handleGrantPermission || $[121] !== t) {
    t41 = () => <motion.div animate={phaseAnimate} className="flex flex-col items-center gap-6 text-center" exit={phaseExit} initial={phaseInitial} key="consent-declined" transition={phaseTransition}><div className="bg-muted flex h-16 w-16 items-center justify-center rounded-full"><ShieldCheck className="text-muted-foreground h-8 w-8" /></div><div className="flex flex-col gap-2"><Text component="h1" text={t("wellnessSteps.intake.consent.declinedTitle")} variant="heading-xl" /><Text component="p" text={t("wellnessSteps.intake.consent.declinedMessage")} variant="body-sm" /></div><div className="flex flex-col gap-4"><Button type="button" onClick={handleGrantPermission}><Text component="span" text={t("wellnessSteps.intake.consent.grantPermission")} /></Button></div></motion.div>;
    $[120] = handleGrantPermission;
    $[121] = t;
    $[122] = t41;
  } else {
    t41 = $[122];
  }
  const renderConsentDeclinedPhase = t41;
  let t42;
  if ($[123] !== t) {
    t42 = () => <div className="flex flex-col items-center gap-3 pt-2"><div className="text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0" /><Text component="span" text={t("wellnessSteps.intake.privacy")} tone="muted" variant="caption" /></div></div>;
    $[123] = t;
    $[124] = t42;
  } else {
    t42 = $[124];
  }
  const renderPrivacy = t42;
  const renderBackground = _temp4;
  const t43 = "relative flex min-h-[calc(100dvh-128px)] w-full flex-col items-center justify-center px-6 pt-8 pb-12";
  let t44;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = renderBackground();
    $[125] = t44;
  } else {
    t44 = $[125];
  }
  const t45 = "relative z-10 flex w-full max-w-md flex-col gap-6";
  let t46;
  if ($[126] !== consentDeclined || $[127] !== renderHeader || $[128] !== resolvedPhase) {
    t46 = resolvedPhase === "guidance" && !consentDeclined && renderHeader();
    $[126] = consentDeclined;
    $[127] = renderHeader;
    $[128] = resolvedPhase;
    $[129] = t46;
  } else {
    t46 = $[129];
  }
  const T0 = AnimatePresence;
  const t47 = "wait";
  let t48;
  if ($[130] !== consentDeclined || $[131] !== renderConsentDeclinedPhase) {
    t48 = consentDeclined && renderConsentDeclinedPhase();
    $[130] = consentDeclined;
    $[131] = renderConsentDeclinedPhase;
    $[132] = t48;
  } else {
    t48 = $[132];
  }
  let t49;
  if ($[133] !== consentDeclined || $[134] !== renderGuidancePhase || $[135] !== resolvedPhase) {
    t49 = !consentDeclined && resolvedPhase === "guidance" && renderGuidancePhase();
    $[133] = consentDeclined;
    $[134] = renderGuidancePhase;
    $[135] = resolvedPhase;
    $[136] = t49;
  } else {
    t49 = $[136];
  }
  let t50;
  if ($[137] !== consentDeclined || $[138] !== renderCroppingPhase || $[139] !== resolvedPhase) {
    t50 = !consentDeclined && resolvedPhase === "cropping" && renderCroppingPhase();
    $[137] = consentDeclined;
    $[138] = renderCroppingPhase;
    $[139] = resolvedPhase;
    $[140] = t50;
  } else {
    t50 = $[140];
  }
  const t51 = !consentDeclined && (resolvedPhase === "validating" || resolvedPhase === "validated" || resolvedPhase === "validation-warning") && renderValidationPhase();
  let t52;
  if ($[141] !== consentDeclined || $[142] !== isError || $[143] !== renderErrorPhase) {
    t52 = !consentDeclined && isError && renderErrorPhase();
    $[141] = consentDeclined;
    $[142] = isError;
    $[143] = renderErrorPhase;
    $[144] = t52;
  } else {
    t52 = $[144];
  }
  let t53;
  if ($[145] !== T0 || $[146] !== t48 || $[147] !== t49 || $[148] !== t50 || $[149] !== t51 || $[150] !== t52) {
    t53 = <T0 mode={t47}>{t48}{t49}{t50}{t51}{t52}</T0>;
    $[145] = T0;
    $[146] = t48;
    $[147] = t49;
    $[148] = t50;
    $[149] = t51;
    $[150] = t52;
    $[151] = t53;
  } else {
    t53 = $[151];
  }
  let t54;
  if ($[152] !== consentDeclined || $[153] !== renderPrivacy) {
    t54 = !consentDeclined && renderPrivacy();
    $[152] = consentDeclined;
    $[153] = renderPrivacy;
    $[154] = t54;
  } else {
    t54 = $[154];
  }
  let t55;
  if ($[155] !== t46 || $[156] !== t53 || $[157] !== t54) {
    t55 = <div className={t45}>{t46}{t53}{t54}</div>;
    $[155] = t46;
    $[156] = t53;
    $[157] = t54;
    $[158] = t55;
  } else {
    t55 = $[158];
  }
  const t56 = consent.isVerifying || isRegionPending || !dataPrivacyRegion;
  let t57;
  if ($[159] !== consent) {
    t57 = open => {
      if (!open) {
        consent.handleConsentCancel();
      }
    };
    $[159] = consent;
    $[160] = t57;
  } else {
    t57 = $[160];
  }
  let t58;
  if ($[161] !== biometricConsentItems || $[162] !== consent.consentChecked || $[163] !== consent.handleConsentAgree || $[164] !== consent.handleConsentCancel || $[165] !== consent.setConsentChecked || $[166] !== consentTranslationKeys || $[167] !== showConsentOverlay || $[168] !== t56 || $[169] !== t57) {
    t58 = <ConsentOverlay consentChecked={consent.consentChecked} isVerifying={t56} open={showConsentOverlay} biometricConsentItems={biometricConsentItems} translationKeys={consentTranslationKeys} onCancel={consent.handleConsentCancel} onConfirm={consent.handleConsentAgree} onConsentChange={consent.setConsentChecked} onOpenChange={t57} />;
    $[161] = biometricConsentItems;
    $[162] = consent.consentChecked;
    $[163] = consent.handleConsentAgree;
    $[164] = consent.handleConsentCancel;
    $[165] = consent.setConsentChecked;
    $[166] = consentTranslationKeys;
    $[167] = showConsentOverlay;
    $[168] = t56;
    $[169] = t57;
    $[170] = t58;
  } else {
    t58 = $[170];
  }
  let t59;
  if ($[171] !== t44 || $[172] !== t55 || $[173] !== t58) {
    t59 = <div className={t43}>{t44}{t55}{t58}</div>;
    $[171] = t44;
    $[172] = t55;
    $[173] = t58;
    $[174] = t59;
  } else {
    t59 = $[174];
  }
  return t59;
};
function _temp(query) {
  const isValidated = query.state.data?.isValidated;
  return isValidated ? false : POLL_INTERVAL;
}
function _temp2(p_0) {
  return {
    id: p_0.id,
    label: p_0.label,
    thumbnailUrl: p_0.wellnessImageUrl
  };
}
function _temp3(issue) {
  return <Text component="p" key={issue} text={issue} tone="muted" variant="body-sm" />;
}
function _temp4() {
  return <div aria-hidden={true} className="fixed"><svg className="h-[150px] w-full opacity-[0.05]" fill="none" viewBox="0 0 800 200" xmlns="http://www.w3.org/2000/svg"><path d="M-50 120C100 40 200 160 400 100C600 40 700 140 850 80" stroke="hsl(var(--primary))" strokeWidth="2.5" /><path d="M-50 150C150 70 250 180 450 120C650 60 750 160 850 100" stroke="hsl(var(--primary))" strokeWidth="1.5" /></svg><svg className="h-[150px] w-full opacity-[0.04]" fill="none" viewBox="0 0 800 200" xmlns="http://www.w3.org/2000/svg"><path d="M-50 120C100 40 200 160 400 100C600 40 700 140 850 80" stroke="hsl(var(--primary))" strokeWidth="2.5" /></svg><svg className="h-[150px] w-full opacity-[0.04]" fill="none" viewBox="0 0 800 200" xmlns="http://www.w3.org/2000/svg"><path d="M-50 120C100 40 200 160 400 100C600 40 700 140 850 80" stroke="hsl(var(--primary))" strokeWidth="2.5" /></svg></div>;
}